import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import shiatsuHero from "@/assets/shiatsu-hero.webp";
import productShowcase2 from "@/assets/product-showcase-2.webp";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart } = useCartStore();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);

  useEffect(() => {
    if (isOpen) syncCart();
  }, [isOpen, syncCart]);

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
      setIsOpen(false);
    }
  };

  const getCartImageSrc = (item: typeof items[number]) => {
    const imageUrl = item.product.node.images?.edges?.[0]?.node?.url?.trim();
    if (imageUrl) return imageUrl;

    const productKey = `${item.product.node.handle} ${item.product.node.title}`.toLowerCase();
    if (productKey.includes("deeprelief") || productKey.includes("shiatsu")) {
      return shiatsuHero;
    }

    return productShowcase2;
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="relative rounded-full border-border">
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full border-0 p-0 text-xs text-white gradient-cta">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex h-full w-full flex-col p-0 sm:max-w-lg">
        <SheetHeader className="flex-shrink-0 border-b border-border px-6 pt-6 pb-4">
          <SheetTitle className="text-xl">Your Cart ({totalItems})</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your cart is empty" : "Free shipping on all orders 🇬🇧"}
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          {items.length === 0 ? (
            <div className="flex flex-1 items-center justify-center px-6">
              <div className="text-center">
                <ShoppingCart className="mx-auto mb-4 h-16 w-16 text-muted-foreground opacity-40" />
                <p className="mb-1 text-lg font-medium text-foreground">Your cart is empty</p>
                <p className="text-sm text-muted-foreground">Discover our bestselling massagers</p>
              </div>
            </div>
          ) : (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                  {items.map((item) => {
                    const fallbackSrc = item.product.node.title.toLowerCase().includes("deeprelief") || item.product.node.handle.toLowerCase().includes("shiatsu")
                      ? shiatsuHero
                      : productShowcase2;

                    return (
                      <div key={item.variantId} className="flex gap-4 rounded-xl border border-border/50 bg-secondary/30 p-3">
                        <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-lg border border-border/30 bg-background">
                          {item.product.node.images?.edges?.[0]?.node ? (
                            <img
                              src={getCartImageSrc(item)}
                              alt={item.product.node.title}
                              className="h-full w-full object-contain p-1"
                              onError={(event) => {
                                if (event.currentTarget.src !== fallbackSrc) {
                                  event.currentTarget.src = fallbackSrc;
                                }
                              }}
                            />
                          ) : (
                            <img
                              src={fallbackSrc}
                              alt={item.product.node.title}
                              className="h-full w-full object-contain p-1"
                            />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="mb-1 text-sm font-semibold leading-tight text-foreground">{item.product.node.title}</h4>
                          <p className="mb-2 text-xs text-muted-foreground">
                            {item.selectedOptions.map((o) => o.value).join(' • ')}
                            {(item.selectedOptions.some(o => o.value.includes("Duo")) ? " (2 units)" : 
                              item.selectedOptions.some(o => o.value.includes("Family")) ? " (3 units)" : "")}
                          </p>
                          <p className="font-bold text-foreground">£{parseFloat(item.price.amount).toFixed(2)}</p>
                        </div>
                        <div className="flex flex-shrink-0 flex-col items-end justify-between">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeItem(item.variantId)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          <div className="flex items-center gap-1.5 rounded-full border border-border bg-background px-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={() => updateQuantity(item.variantId, item.quantity - 1)}>
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={() => updateQuantity(item.variantId, item.quantity + 1)}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex-shrink-0 border-t border-border/50 px-6 py-3">
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-cta" /> Free Shipping</span>
                  <span className="flex items-center gap-1"><RotateCcw className="h-3.5 w-3.5 text-cta" /> 30-Day Returns</span>
                  <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-cta" /> Secure Checkout</span>
                </div>
              </div>

              <div className="flex-shrink-0 space-y-3 border-t border-border bg-background px-6 pt-4 pb-6">
                <div className="flex items-center justify-between">
                  <span className="text-base font-medium text-muted-foreground">Subtotal</span>
                  <span className="text-xl font-bold text-foreground">£{totalPrice.toFixed(2)}</span>
                </div>
                <Button onClick={handleCheckout} className="gradient-cta h-14 w-full rounded-full text-lg font-semibold text-white hover:opacity-90" size="lg" disabled={items.length === 0 || isLoading || isSyncing}>
                  {isLoading || isSyncing ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ExternalLink className="mr-2 h-5 w-5" />Secure Checkout</>}
                </Button>
                <p className="text-center text-xs text-muted-foreground">🔒 Secure payments</p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};